import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { cn } from "@/lib/utils";

interface CrosswordCellProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onAutoAdvance?: () => void;
  isActive?: boolean;
  tabIndex?: number;
  dimmed?: boolean;
  linkColor?: string | null;
  hasConstraint?: boolean;
}

export interface CrosswordCellRef {
  focus: () => void;
  select: () => void;
}

export const CrosswordCell = forwardRef<CrosswordCellRef, CrosswordCellProps>(({
  value,
  onChange,
  onKeyDown,
  onAutoAdvance,
  isActive = false,
  tabIndex = 0,
  dimmed = false,
  linkColor = null,
  hasConstraint = false,
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    select: () => inputRef.current?.select(),
  }));

  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isActive]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Filter to only allow Swedish letters (a-z, å, ä, ö) and period (.)
    const lastChar = inputValue.slice(-1);
    const isValidLetter = /^[a-zA-ZåäöÅÄÖ]$/.test(lastChar);
    const isPeriod = lastChar === '.';
    
    if (isValidLetter) {
      const newValue = lastChar.toUpperCase();
      onChange(newValue);
      // Trigger auto-advance after setting the value
      onAutoAdvance?.();
    } else if (isPeriod) {
      onChange('.');
      // Don't auto-advance for period - it marks the end
    } else if (inputValue === '') {
      onChange('');
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Select content on focus for easy replacement
    inputRef.current?.select();
  };

  return (
    <div className="relative">
      <div
        className={cn(
          "crossword-cell cursor-text",
          value && "crossword-cell-filled",
          isFocused && "border-foreground",
          dimmed && "opacity-40",
          linkColor && "ring-2 ring-offset-1",
          hasConstraint && "bg-primary/10"
        )}
        style={linkColor ? { 
          borderColor: linkColor,
          boxShadow: `0 0 0 2px ${linkColor}40, inset 0 0 0 1px ${linkColor}`
        } : undefined}
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          onBlur={() => setIsFocused(false)}
          tabIndex={tabIndex}
          className="w-full h-full bg-transparent text-center outline-none uppercase font-medium text-xs"
          maxLength={2}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
        />
      </div>
    </div>
  );
});

CrosswordCell.displayName = "CrosswordCell";
